// src/pages/om/PlanPage.js
// 运维中心 · 计划（doc01 2.16，缺陷 103/104）
// 结构：头部「计划」+ 右上「新建计划」→ 搜索行（按计划名称/所属电站进行搜索 + 查询/重置）
//      → 表格（计划名称/站点信息/计划类型/首次处理时间/周期/操作）。
// 操作：详情 / 编辑 / 删除；新建/查询/重置均真实可用（本地增删改 + message）。
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table, Input, Button, Space, Modal, Form, Select, Popconfirm, Tag, message,
} from 'antd';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import { STATIONS, OM_PLAN_ROWS, OM_PLAN_KINDS, OM_PLAN_CYCLES } from '../../config/stationData';

const PLAN_TYPES = OM_PLAN_KINDS.map((k) => ({ kind: k, label: `${k}计划` }));
const KIND_COLOR = { 巡视检查: 'blue', 维护保养: 'cyan' };
const CYCLE_COLOR = { '每日': 'green', '每周': 'blue', '1个月': 'cyan', '3个月': 'purple', '6个月': 'orange', '12个月': 'red' };
const OWNERS = ['运维部 / 张伟', '运维部 / 李娜', '运维部 / 李文杰', '工程部 / 王强', '工程部 / 周翔宇'];
const STATES = ['执行中', '待启动'];

const KV = ({ items }) => (
  <div className="dev-kv">
    {items.map(([k, v]) => (
      <div className="dev-kv-row" key={k}>
        <span className="dev-kv-k">{k}</span>
        <span className="dev-kv-v">{v == null || v === '' ? '-' : v}</span>
      </div>
    ))}
  </div>
);

const PlanPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/om/plan');

  const [list, setList] = useState(() => OM_PLAN_ROWS.map((p) => ({ ...p })));
  const [draft, setDraft] = useState('');
  const [applied, setApplied] = useState('');
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const stationOptions = useMemo(() => STATIONS.map((s) => ({ value: s.name, label: s.name })), []);

  const match = (p) => {
    if (!applied) return true;
    const kw = applied.trim().toLowerCase();
    return String(p.name || '').toLowerCase().includes(kw) || String(p.stations || '').toLowerCase().includes(kw);
  };

  const columns = [
    { title: '计划名称', dataIndex: 'name', width: 220, render: (v) => <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span> },
    { title: '站点信息', dataIndex: 'stations', width: 220 },
    {
      title: '计划类型', dataIndex: 'kind', width: 140,
      render: (v) => <Tag color={KIND_COLOR[v] || 'default'} style={{ margin: 0 }}>{v}计划</Tag>,
    },
    { title: '首次处理时间', dataIndex: 'firstAt', width: 180 },
    {
      title: '周期', dataIndex: 'cycle', width: 110,
      render: (v) => <Tag color={CYCLE_COLOR[v] || 'default'} style={{ margin: 0 }}>{v}</Tag>,
    },
    {
      title: '操作', key: 'op', width: 180, fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          <Button type="link" size="small" onClick={() => setDetail(r)}>详情</Button>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm
            title="删除计划"
            description={`确认删除计划「${r.name}」？删除后不可恢复。`}
            okText="删除" cancelText="取消" okButtonProps={{ danger: true }}
            onConfirm={() => removeRow(r)}
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ kind: OM_PLAN_KINDS[0], cycle: '每周', stations: [STATIONS[0]?.name], owner: OWNERS[0], state: '待启动' });
    setCreating(true);
  };

  function openEdit(r) {
    setEditing(r);
    form.setFieldsValue({
      name: r.name, kind: r.kind, cycle: r.cycle, firstAt: r.firstAt,
      stations: String(r.stations || '').split(/[、,，]\s*/).filter(Boolean),
      owner: r.owner, desc: r.desc, state: r.state,
    });
    setCreating(true);
  }

  const submitForm = async () => {
    const v = await form.validateFields();
    const stations = (v.stations || []).join('、') || '全部站点';
    if (editing) {
      setList((prev) => prev.map((p) => (p.key === editing.key ? { ...p, ...v, stations } : p)));
      message.success('计划已更新');
    } else {
      const key = Date.now();
      setList((prev) => [{
        key, kind: v.kind, name: v.name, desc: v.desc || '', firstAt: v.firstAt,
        cycle: v.cycle, stations, owner: v.owner, next: '生成工单前 1 天 23:59:59', state: v.state || '待启动',
      }, ...prev]);
      message.success('计划已创建，将按周期自动生成工单');
    }
    setCreating(false);
    form.resetFields();
    setEditing(null);
  };

  const removeRow = (r) => {
    setList((prev) => prev.filter((p) => p.key !== r.key));
    message.success('计划已删除');
  };

  const doQuery = () => { setApplied(draft); message.success('已按当前条件搜索'); };
  const doReset = () => { setDraft(''); setApplied(''); message.success('搜索条件已重置'); };

  const actions = [{ key: 'new', label: '新建计划', primary: true }];
  const onAction = (a) => { if (a.key === 'new') openCreate(); };

  const d = detail || {};

  return (
    <>
      <PageSkeleton
        title={meta ? pick(meta.label, lang) : '计划'}
        desc={meta ? pick(meta.desc, lang) : ''}
        crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
        filters={[]}
        actions={actions}
        onAction={onAction}
      >
        <div className="filter-bar">
          <div className="filter-bar-fields">
            <div className="filter-item">
              <Input
                allowClear
                style={{ width: 320 }}
                placeholder="按计划名称/所属电站进行搜索"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={doQuery}
              />
            </div>
          </div>
          <div className="filter-bar-actions">
            <Space>
              <Button type="primary" onClick={doQuery}>查询</Button>
              <Button onClick={doReset}>重置</Button>
            </Space>
          </div>
        </div>

        <Table
          rowKey="key"
          dataSource={list.filter(match)}
          columns={columns}
          pagination={{ pageSize: 10, size: 'small' }}
          size="middle"
          scroll={{ x: 1000 }}
        />
      </PageSkeleton>

      <Modal
        open={!!detail}
        title={`计划详情 · ${d.name || ''}`}
        width={720}
        onCancel={() => setDetail(null)}
        footer={<Button type="primary" onClick={() => setDetail(null)}>关闭</Button>}
      >
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-title">计划信息</div>
          <KV items={[
            ['计划名称', d.name], ['计划类型', d.kind ? `${d.kind}计划` : ''], ['站点信息', d.stations],
            ['首次处理时间', d.firstAt], ['周期', d.cycle], ['责任部门 / 负责人', d.owner],
            ['下次生成工单', d.next], ['状态', d.state], ['计划描述', d.desc],
          ]} />
        </div>
        <div className="blk-note">
          <div className="blk-note-text">按计划自动生成工单：系统于首次处理时间开始前一天的 23:59:59 生成工单进入待接单列表；若首次处理时间为当天则立即生成，此后按周期重复生成；一条计划涉及多个站点时每个站点各生成一条工单。</div>
        </div>
      </Modal>

      <Modal
        open={creating}
        title={editing ? '编辑计划' : '新建计划'}
        onCancel={() => { setCreating(false); setEditing(null); form.resetFields(); }}
        onOk={submitForm}
        okText={editing ? '保存' : '创建'}
        cancelText="取消"
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="计划名称" rules={[{ required: true, message: '请输入计划名称' }]}>
            <Input placeholder="请输入计划名称" maxLength={50} />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="kind" label="计划类型" rules={[{ required: true, message: '请选择计划类型' }]} style={{ width: 190 }}>
              <Select options={PLAN_TYPES.map((t) => ({ value: t.kind, label: t.label }))} />
            </Form.Item>
            <Form.Item name="cycle" label="周期" rules={[{ required: true, message: '请选择周期' }]} style={{ width: 150 }}>
              <Select options={OM_PLAN_CYCLES.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="state" label="状态" style={{ width: 130 }}>
              <Select options={STATES.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
          </Space>
          <Form.Item
            name="firstAt" label="首次处理时间" tooltip="起始日期不能早于当日，时间精确到分钟，默认 24 小时后结束"
            rules={[{ required: true, message: '请输入首次处理时间' }]}
          >
            <Input placeholder="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item name="stations" label="涉及站点" rules={[{ required: true, message: '请选择涉及站点' }]}>
            <Select mode="multiple" options={stationOptions} placeholder="可多选，一个站点生成一条工单" />
          </Form.Item>
          <Form.Item name="owner" label="责任部门 / 负责人" rules={[{ required: true, message: '请选择负责人' }]}>
            <Select showSearch options={OWNERS.map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="desc" label="计划描述（≤ 50 字）">
            <Input.TextArea rows={2} maxLength={50} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PlanPage;
